import { Injectable } from '@angular/core';
import { Http, RequestOptions, Headers, Response } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import { LocalStorageService } from './localstorage.service';
import { environment } from '../../environments/environment';
import { Communities } from '../services/communities.service';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/observable/throw';
import 'rxjs/add/observable/fromPromise';


@Injectable()
export class ApiService {

  private _apiRoot: string = environment.apiUrl;

  constructor(
    public _http: Http,
    public _storage: LocalStorageService,
    private _communities: Communities
  ) { }

  private _baseUrl(path: string): string {
    return this._apiRoot + path;
  }

  private _buildHeaders(multipart = false): Headers {
    let token = this._storage.get('token');
    let headers = {}
    headers['Content-Type'] = 'application/json';
    if (multipart) {
      // headers['Content-Type'] = 'multipart/form-data';
      headers = {}
    }
    headers['Accept-Language'] = this._communities.lang;
    if (token) {
      headers['Authorization'] = `Token ${token}`;
    }
    return new Headers(headers);
  }

  private _buildOptions(multipart = false): RequestOptions {
    let headers = this._buildHeaders(multipart);
    let options = new RequestOptions({ headers: headers });
    return options;
  }

  private _handleError(error: Response): Observable<object> {
    return Observable.throw(error.json());
  }

  private _encodeParams(params: object): string {
    return Object.keys(params).map(
      (key) => {
        if (params[key] instanceof Array) {
          return params[key].map((v) => `${key}=${encodeURIComponent(v)}`).join('&');
        } else {
          return `${key}=${encodeURIComponent(params[key])}`
        }
      }
    ).join('&');
  }

  private _buildUrl(url: string, params: object): string {
    let getParams: string = this._encodeParams(params);
    if (getParams) {
      return url + '?' + this._encodeParams(params);
    } else {
      return url;
    }
  }

  private _get(url: string): Observable<any> {
    let options = this._buildOptions();
    return this._http.get(url, options)
      .map((res: Response) => res.json())
      .catch((error: Response) => this._handleError(error));
  }

  private _post(url: string, data: object = {}, multipart = false): Observable<any> {
    let options = this._buildOptions(multipart);
    return this._http.post(url, data, options)
      .map((res: Response) => res.json())
      .catch((error: Response) => this._handleError(error));
  }

  private _put(url: string, data: object = {}): Observable<any> {
    let options = this._buildOptions();
    return this._http.put(url, data, options)
      .map((res: Response) => res.json())
      .catch((error: Response) => this._handleError(error));
  }

  private _patch(url: string, data: object = {}, multipart = false): Observable<any> {
    let options = this._buildOptions(multipart);
    return this._http.patch(url, data, options)
      .map((res: Response) => res.json())
      .catch((error: Response) => this._handleError(error));
  }

  private _delete(url: string): Observable<any> {
    let options = this._buildOptions();
    return this._http.delete(url, options)
      .map((res: Response) => res.json())
      .catch((error: Response) => this._handleError(error));
  }

  private _retrieve(path: string) {
    return (id: number | string, params: Object = {}): Observable<any> => {
      let url = this._buildUrl(this._baseUrl(path) + '/' + id, params);
      return this._get(url);
    }
  }

  private _update(path: string) {
    return (id: number | string, data: Object = {}, params: Object = {}): Observable<any> => {
      let url = this._buildUrl(this._baseUrl(path) + '/' + id, params);
      return this._put(url, data);
    }
  }

  private _partialUpdate(path: string) {
    return (id: number | string, data: Object = {}, params: Object = {}, multipart = false): Observable<any> => {
      let url = this._buildUrl(this._baseUrl(path) + '/' + id, params);
      return this._patch(url, data, multipart);
    }
  }

  private _destroy(path: string) {
    return (id: number | string, params: Object = {}): Observable<any> => {
      let url = this._buildUrl(this._baseUrl(path) + '/' + id, params);
      return this._delete(url);
    }
  }

  private _create(path: string) {
    return (data: Object = {}, params: Object = {}, multipart = false, additionnalUrl: string = ''): Observable<any> => {
      let url = this._buildUrl(this._baseUrl(path) + (additionnalUrl ? '/' + additionnalUrl : ''), params);
      return this._post(url, data, multipart);
    }
  }

  private _list(path: string) {
    return (params: Object = {}): Observable<any> => {
      let url = this._buildUrl(this._baseUrl(path), params);
      return this._get(url);
    }
  }

  private _getEndpoint(path: string) {
    let that = this;
    return {
      retrieve: this._retrieve(path),
      partialUpdate: this._partialUpdate(path),
      update: this._update(path),
      destroy: this._destroy(path),
      create: this._create(path),
      list: this._list(path),
    }
  }

  login = this._getEndpoint('/v1/token');
  permissions = this._getEndpoint('/v1/communities');
  members = this._getEndpoint('/v1/users');
  me = this._getEndpoint('/v1/users');
  resetPassword = this._getEndpoint('v1/reset-password');

  boards = this._getEndpoint('/v1/stratbox/boards');
  projectBoards = this._getEndpoint('/v1/stratbox/projectboards');
  publicProjectBoards = this._getEndpoint('/v1/stratbox/public/projectboards');
  boardPreview = this._getEndpoint('/v1/stratbox/board-preview');
  boardCategories = this._getEndpoint('/v1/stratbox/categories');


  boardSpaces = this._getEndpoint('/v1/stratbox/boardspaces');
  publicPostits = this._getEndpoint('/v1/stratbox/public/postits');
  postits = this._getEndpoint('/v1/stratbox/postits');
  postitComments = this._getEndpoint('/v1/stratbox/postitcomments');
  postitLikes = this._getEndpoint('/v1/stratbox/postitlikes');
  projectBoardMembers = this._getEndpoint('/v1/stratbox/projectboardmembers');

  categories = this._getEndpoint('/v1/categories');
  labels = this._getEndpoint('/v1/labels');
  posts = this._getEndpoint('/v1/posts');
  comments = this._getEndpoint('/v1/comments');

  keycontents = this._getEndpoint('/v1/keycontents');
  communities = this._getEndpoint('/v1/communities');

  pdfs = this._getEndpoint('/v1/stratbox/projectboards');

  zenbaia = this._getEndpoint('/v1/stratbox/zenbaia-resume');
  accessZenbaia = this._getEndpoint('/v1/stratbox/can-access-zenbaia');
}


// WEBPACK FOOTER //
// /mnt/disk/stratbox.dev/src/app/services/api.service.ts